/*
 * @Description: AdvantagePart组件 - 展示镜片优势信息
 * 从Vue组件转换而来，支持响应式设计，PC端和移动端不同布局
 */
'use client';

import Image from '@/components/Image';
import { useMediaQuery } from '@mui/material';
import React from 'react';

// 定义数据项的类型
interface AdvantageItem {
  image: string;
  title: string;
  title_m?: string;
}

// 定义组件props的类型
interface AdvantageData {
  data_array: AdvantageItem[];
}

interface AdvantagePartProps {
  advantageData: AdvantageData;
}

/**
 * AdvantagePart组件 - 展示镜片优势信息
 * @param advantageData - 包含优势数据的对象
 */
const AdvantagePart: React.FC<AdvantagePartProps> = ({ advantageData }) => {
  // 获取设备类型
  const isMobileScreen = useMediaQuery('(max-width: 768px)');

  // 如果没有数据，不渲染组件
  if (!advantageData?.data_array?.length) {
    return null;
  }

  // PC端布局
  const renderDesktopLayout = () => (
    <div className="lenses-advantage w-full px-[240px] xl:px-[240px] lg:px-[160px] md:px-[120px] box-border my-[120px] xl:my-[120px] lg:my-[96px] md:my-[80px]">
      <ul className="flex justify-between items-start flex-nowrap gap-[24px] xl:gap-[24px] lg:gap-[20px] md:gap-[16px]">
        {advantageData.data_array.map((item, index) => (
          <li
            key={index}
            className="w-[464px] xl:w-[464px] lg:w-[380px] md:w-[320px] p-[45px_20px] xl:p-[45px_20px] lg:p-[36px_16px] md:p-[30px_12px] box-border bg-[#F4F4F4] rounded-[12px] xl:rounded-[12px] lg:rounded-[10px] md:rounded-[8px]"
          >
            <div className="advantage-item-box flex flex-col items-center justify-center">
              <div className="advantage-item-box-top">
                <Image
                  src={item.image}
                  alt={item.title}
                  width={82}
                  height={82}
                  className="w-[82px] xl:w-[82px] lg:w-[68px] md:w-[56px] h-[82px] xl:h-[82px] lg:h-[68px] md:h-[56px] object-cover block mb-[20px] xl:mb-[20px] lg:mb-[16px] md:mb-[12px]"
                />
              </div>
              <div className="advantage-item-box-bottom">
                <h3
                  className="text-[20px] md:text-[1vw] font-bb text-center whitespace-nowrap"
                  dangerouslySetInnerHTML={{ __html: item.title }}
                />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  // 移动端布局
  const renderMobileLayout = () => (
    <div className="lenses-advantage p-[0_14px_40px] box-border">
      <ul className="flex justify-between items-start flex-wrap rounded-[6px] bg-[#F4F4F4] p-[15px_10px_13px]">
        {advantageData.data_array.map((item, index) => {
          const currentTitle = item.title_m || item.title;
          const currentAlt = item.title_m || item.title;

          return (
            <li key={index} className="w-[95px]">
              <div className="advantage-item-box flex flex-col items-center justify-center">
                <div className="advantage-item-box-top">
                  <Image
                    src={item.image}
                    alt={currentAlt}
                    width={41}
                    height={41}
                    className="w-[41px] h-[41px] object-cover block mb-[10px]"
                  />
                </div>
                <div className="advantage-item-box-bottom">
                  <h3
                    className="text-[12px] leading-[14px] text-center"
                    dangerouslySetInnerHTML={{ __html: currentTitle }}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );

  // 根据设备类型渲染不同布局
  return isMobileScreen ? renderMobileLayout() : renderDesktopLayout();
};

export default AdvantagePart;
